import React from 'react';
import SmartIndication from '@svg/smart-indication.svg?react';
import Failure from '@svg/failure.svg?react';
import Warning from '@svg/warning-circle.svg?react';
import Scheduled from '@svg/calendar-clock.svg?react';
import Arrow from '@svg/arrow-right-fill.svg?react';
import { Tooltip } from 'antd';
import './connection-policy-info-cell.scss';
import { LevelName, VersionLabel } from '../../../../../utils/enums';
import { IconWithTooltip } from './icon-with-tooltip';
import { Player } from '@lottiefiles/react-lottie-player';
import loaderAnimation from '../../../../../assets/lottie/md-loader.json';
import smartAnimation from '../../../../../assets/lottie/smart.json';
import { ConnectionModel } from '../../connection.model';
import PolicyStatusLabel from '../../../../shared/policy-status-display/policy-status-label';

const SmartAnimation = () => <Player autoplay={true} loop={true} src={smartAnimation}/>;
const LoaderAnimation = () => <Player autoplay={true} loop={true} src={loaderAnimation}/>;

const ConnectionPolicyInfoCell: React.FC<{ connection: ConnectionModel }> = ({connection}) => {

    const tooltipContent = connection.policyName || connection.policyVersionComments ? <>
        {!!connection.policyName && <div className={'tooltip-content-policy-name'}>{connection.policyName}</div>}
        {!!connection.policyVersionComments && <>
            <span className={'tooltip-content-policy-comments-label'}>comments:</span>
            <div className={'tooltip-content-policy-comments'}>{connection.policyVersionComments}</div>
        </>}
    </>: null;

    const initialTooltipContent = <>
        <span className="bold">Initial policy</span>
        <span>- A basic policy consisting of a global IP block list powered by AT&T.</span>
        <span className="inline-block">It is highly recommended to either assign one of AT&T’s ready-made policies or create a custom policy tailored to your needs.</span>
    </>;

    return (
        connection.serviceLevel === LevelName.BRONZE ? null :
            <div className={'connection-policy-info-cell'}>
                {(connection.isInAnalysis || connection.isSmartDraftInProgress) &&
                    <Tooltip overlayClassName="tooltip-wrapper network-analysis-tooltip"
                             title={connection.isInAnalysis && connection.analysisDaysLeft > 0
                                 ? `Network analysis ends in ${connection.analysisDaysLeft} days.`
                                 : 'Network analysis ends today'}
                             placement={'bottom'}>
                        <span className="left-icon">
                            <SmartAnimation />
                        </span>
                    </Tooltip>
                }
                {connection.isFirstSmartDraft &&
                    <IconWithTooltip
                        tooltipClassName="smart-protection-tooltip left-icon"
                        iconClassName="left-icon"
                        title="Smart Protection draft"
                        IconComponent={SmartIndication}
                    />
                }
                {connection.policyStatus === VersionLabel.Pending && (
                    <span className="left-icon">
                            <LoaderAnimation/>
                        </span>
                )}
                {!!connection.policyStatus && <Tooltip title={tooltipContent} placement={'bottom'} overlayClassName="indication-tooltip tooltip-wrapper connection-policy-tooltip">
                    <span><PolicyStatusLabel label={connection.policyStatus} versionNumber={connection.policyVersionNumber} /></span>
                </Tooltip>}
                {connection.hasFailure && (
                    <IconWithTooltip
                        tooltipClassName="failure-tooltip"
                        title={'Apply policy failed'}
                        IconComponent={Failure}
                    />
                )}
                {connection.isInitial && !connection.hasFailure && (
                    <IconWithTooltip
                        tooltipClassName="initial-tooltip"
                        title={initialTooltipContent}
                        IconComponent={Warning}
                    />
                )}
                {connection.policyStatus !== VersionLabel.Initial && <span className={'version-name'}>{connection.policyName}</span>}
                {connection.isTargetSchedule && (
                    <IconWithTooltip
                        tooltipClassName="scheduled-info-tooltip"
                        iconClassName={`schedule-icon target ${connection.isUpcomingSchedule ? 'upcoming' : ''}`}
                        title={`Apply policy scheduled to ${connection.scheduleDate}`}
                        IconComponent={Scheduled}
                    />
                )}
                {connection.isSourceSchedule &&
                    <>
                        <IconWithTooltip
                            tooltipClassName="source-scheduled-info-tooltip"
                            iconClassName="schedule-icon source"
                            title={`This policy is scheduled for apply to other connections at: ${connection.scheduleDate}`}
                            IconComponent={Scheduled}
                        />
                        <Arrow className="arrow-icon"/>
                    </>
                }
            </div>
    );
};

export default ConnectionPolicyInfoCell;